import { useState, useEffect, useRef, useCallback } from 'react';
import { Howl } from 'howler';

export const useAudioPlayer = () => {
  const [currentTrack, setCurrentTrack] = useState(null);
  const [currentAlbum, setCurrentAlbum] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(30); // Default 30s preview
  const [volume, setVolume] = useState(0.8);
  const [error, setError] = useState(null);
  
  const soundRef = useRef(null);
  const progressIntervalRef = useRef(null);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (soundRef.current) {
        soundRef.current.unload();
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  // Update progress while playing
  const startProgressTracking = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
    
    progressIntervalRef.current = setInterval(() => {
      if (soundRef.current && soundRef.current.playing()) {
        const seek = soundRef.current.seek();
        const dur = soundRef.current.duration();
        if (dur > 0) {
          setProgress((seek / dur) * 100);
        }
      }
    }, 100);
  }, []);

  const stopProgressTracking = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  // Play a track
  const playTrack = useCallback((track, album) => {
    // Check if preview URL exists
    if (!track.previewUrl) {
      setError('Preview not available');
      return;
    }

    // If same track, just toggle play/pause
    if (currentTrack?.title === track.title && soundRef.current) {
      if (isPlaying) {
        soundRef.current.pause();
        setIsPlaying(false);
        stopProgressTracking();
      } else {
        soundRef.current.play();
        setIsPlaying(true);
        startProgressTracking();
      }
      return;
    }

    // Stop current track
    if (soundRef.current) {
      soundRef.current.unload();
    }
    stopProgressTracking();
    setError(null);
    setIsLoading(true);
    setProgress(0);

    // Create new Howl instance
    soundRef.current = new Howl({
      src: [track.previewUrl],
      html5: true, // Use HTML5 Audio for streaming
      volume: volume,
      onload: () => {
        setIsLoading(false);
        setDuration(soundRef.current.duration());
      },
      onplay: () => {
        setIsPlaying(true);
        startProgressTracking();
      },
      onpause: () => {
        setIsPlaying(false);
        stopProgressTracking();
      },
      onend: () => {
        setIsPlaying(false);
        setProgress(100);
        stopProgressTracking();
      },
      onloaderror: (id, err) => {
        console.error('Load error:', err);
        setError('Failed to load preview');
        setIsLoading(false);
        setIsPlaying(false);
      },
      onplayerror: (id, err) => {
        console.error('Play error:', err);
        setError('Failed to play preview');
        setIsPlaying(false);
      },
    });

    setCurrentTrack(track);
    setCurrentAlbum(album);
    soundRef.current.play();
  }, [currentTrack, isPlaying, volume, startProgressTracking, stopProgressTracking]);

  // Play first track of an album
  const playAlbum = useCallback((album) => {
    if (album.tracks && album.tracks.length > 0) {
      const firstTrackWithPreview = album.tracks.find(t => t.previewUrl);
      if (firstTrackWithPreview) {
        playTrack(firstTrackWithPreview, album);
      } else {
        setError('No previews available for this album');
      }
    }
  }, [playTrack]);

  // Toggle play/pause
  const togglePlayPause = useCallback(() => {
    if (!soundRef.current) return;
    
    if (isPlaying) {
      soundRef.current.pause();
    } else {
      soundRef.current.play();
    }
  }, [isPlaying]);

  // Seek to position
  const seek = useCallback((percentage) => {
    if (!soundRef.current) return;
    
    const dur = soundRef.current.duration();
    const seekTime = (percentage / 100) * dur;
    soundRef.current.seek(seekTime);
    setProgress(percentage);
  }, []);

  // Set volume
  const updateVolume = useCallback((newVolume) => {
    setVolume(newVolume);
    if (soundRef.current) {
      soundRef.current.volume(newVolume);
    }
  }, []);

  // Stop playback
  const stop = useCallback(() => {
    if (soundRef.current) {
      soundRef.current.stop();
      soundRef.current.unload();
      soundRef.current = null;
    }
    stopProgressTracking();
    setIsPlaying(false);
    setProgress(0);
    setCurrentTrack(null);
    setCurrentAlbum(null);
  }, [stopProgressTracking]);

  // Play next track in album
  const playNext = useCallback(() => {
    if (!currentAlbum || !currentTrack) return;
    
    const currentIndex = currentAlbum.tracks.findIndex(
      t => t.trackNumber === currentTrack.trackNumber
    );
    
    // Find next track with preview
    for (let i = currentIndex + 1; i < currentAlbum.tracks.length; i++) {
      if (currentAlbum.tracks[i].previewUrl) {
        playTrack(currentAlbum.tracks[i], currentAlbum);
        return;
      }
    }
    
    // If no next track, loop to first
    const firstWithPreview = currentAlbum.tracks.find(t => t.previewUrl);
    if (firstWithPreview) {
      playTrack(firstWithPreview, currentAlbum);
    }
  }, [currentAlbum, currentTrack, playTrack]);

  // Play previous track in album
  const playPrevious = useCallback(() => {
    if (!currentAlbum || !currentTrack) return;
    
    const currentIndex = currentAlbum.tracks.findIndex(
      t => t.trackNumber === currentTrack.trackNumber
    );
    
    // Find previous track with preview
    for (let i = currentIndex - 1; i >= 0; i--) {
      if (currentAlbum.tracks[i].previewUrl) {
        playTrack(currentAlbum.tracks[i], currentAlbum);
        return;
      }
    }
    
    // If no previous track, loop to last
    for (let i = currentAlbum.tracks.length - 1; i >= 0; i--) {
      if (currentAlbum.tracks[i].previewUrl) {
        playTrack(currentAlbum.tracks[i], currentAlbum);
        return;
      }
    }
  }, [currentAlbum, currentTrack, playTrack]);

  return {
    // State
    currentTrack,
    currentAlbum,
    isPlaying,
    isLoading,
    progress,
    duration,
    volume,
    error,
    
    // Actions
    playTrack,
    playAlbum,
    togglePlayPause,
    seek,
    updateVolume,
    stop,
    playNext,
    playPrevious,
  };
};
